import { useElementOrFindIt } from '../core';

import { SELECTORS } from './constants';
import { LeftNavLayout } from './leftnav-layout';
import { LeftNavMenu } from './leftnav-menu';

const INIT_ERROR_MSG = 'Uxpl.LeftNav: Could not initialize Uxpl.LeftNav.';

export type LeftNavOptions = {
  useLayoutRoot?: Element;
  useMenu?: Element;
  collapse?: Element;
};

export class LeftNav {
  static createWithLayout(layoutRootElem: Element): LeftNav {
    return new LeftNav({ useLayoutRoot: layoutRootElem });
  }

  static createMenuOnly(menuElem: Element): LeftNav {
    return new LeftNav({ useMenu: menuElem });
  }

  layout: LeftNavLayout;
  menu: LeftNavMenu;

  /**
   * Create a new LeftNav.
   *
   * There are two variations for the left nav:
   *  - with layout (can be collapsible): requires the parameter `useLayoutRoot`
   *  - menu only (cannot be collapsible): requires the parameter "useMenu"
   *
   * @param {object} initOptions
   * @param {Element} useLayoutRoot - root element for layout, only required if useMenu is not provided
   * @param {Element} useMenu - menu element, only required if useLayoutRoot is not provided
   * @param {Element} [collapse] - collapse button (optional)
   */
  constructor(options: LeftNavOptions) {
    options = options || {};
    if (!options.useLayoutRoot && !options.useMenu) {
      throw new Error(
        `${INIT_ERROR_MSG} You need to provide either the menu element or the layoutRoot element`
      );
    }

    const layoutRoot = options.useLayoutRoot;
    let menuElem = options.useMenu;
    if (layoutRoot) {
      this.layout = new LeftNavLayout({
        layoutRoot: layoutRoot,
        collapse: options.collapse,
      });
      menuElem = useElementOrFindIt(menuElem, {
        parent: layoutRoot,
        selector: SELECTORS.menu,
      });
    }

    this.menu = new LeftNavMenu(menuElem);
  }

  destroy(): void {
    if (this.menu) {
      this.menu.destroy();
    }

    if (this.layout) {
      this.layout.destroy();
    }
  }
}
